import 'date-fns'
import React, { useState, useCallback } from 'react'
import { withStyles } from '@material-ui/core/styles'
import DateFnsUtils from '@date-io/date-fns'
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers'
import { TextField, IconButton, InputAdornment } from '@material-ui/core'
import ClearIcon from '@material-ui/icons/Clear'
import EventIcon from '@material-ui/icons/Event'
import LabelError from 'app/componentV4/Labels/LabelError'
import debounce from 'lodash.debounce'
import { isNil } from 'ramda'
import LocalizedDatePickerProvider from 'app/components/datepickers/LocalizedDatePickerProvider'
import LocalizedDatePickerToolbar from 'app/components/datepickers/LocalizedDatePickerToolbar'
import { useIntl } from 'react-intl'

const CustomsTextField = withStyles((theme) => ({
  root: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.colors.white,
      color: theme.colors['grey-400'],
      height: '48px',
      padding: '6px 10px',
      '&:hover': {
        backgroundColor: theme.colors['primary-lighter'],
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.colors['grey-100'],
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: `${theme.colors['grey-200']} !important`,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: '1px !important',
        borderColor: `${theme.colors['grey-200']} !important`,
      },
      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: `${theme.colors['red-500']} !important`,
      },
      '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: `${theme.colors['red-500']} !important`,
      },
      '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: `${theme.colors['red-500']} !important`,
      },
      '&.Mui-disabled': {
        backgroundColor: `${theme.colors['neutrals-100']} !important`,
      },
    },
    '& .MuiInputBase-input': {
      padding: '6px',
      color: `${theme.colors.primary} !important`,
    },
  },
}))(TextField)

const SimpleDatePicker = (props) => {
  const {
    id,
    format,
    onChange,
    value,
    error = false,
    helperText = '',
    ...rest
  } = props
  const [open, setOpen] = useState(false)
  const [hover, setHover] = useState(false)

  const { formatMessage } = useIntl()

  const handleDateChange = useCallback(
    debounce((date) => {
      onChange(isNil(date) ? '' : date)
    }, 1500),
    [],
  )

  const handleClear = (e) => {
    e.stopPropagation()
    onChange('')
  }

  const handleOpen = () => {
    setOpen(true)
  }

  return (
    <LocalizedDatePickerProvider>
      <div
        className="w-full"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <KeyboardDatePicker
          inputVariant="outlined"
          format={format}
          fullWidth
          value={value || null}
          onChange={handleDateChange}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          open={open}
          error={error}
          helperText=""
          TextFieldComponent={(textFieldProps) => (
            <CustomsTextField
              {...textFieldProps}
              helperText=""
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {value && (
                      <IconButton
                        onClick={handleClear}
                        size="small"
                        className={hover ? 'opacity-100' : 'opacity-0'}
                      >
                        <ClearIcon className="w-20" />
                      </IconButton>
                    )}
                    <IconButton onClick={handleOpen} size="small">
                      <EventIcon className="w-20" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          ToolbarComponent={LocalizedDatePickerToolbar}
          okLabel={formatMessage({ id: 'date.picker.ok' })}
          cancelLabel={formatMessage({ id: 'date.picker.cancel' })}
          clearLabel={formatMessage({ id: 'date.picker.clear' })}
          {...rest}
        />
        {error && <LabelError id={id} text={helperText} />}
      </div>
    </LocalizedDatePickerProvider>
  )
}

export default SimpleDatePicker
